import { useEffect, useRef, useState } from 'react'

interface IntesectionObserverProps {
  /**
   * Callback to be executed when isIntersecting changes
   *
   * @param {Boolean} isIntersecting Indicates if element is on intersecting
   * @returns {void}
   */
  callback?: (isIntersecting: boolean) => void
  /**
   * IntersectionObserver props ex: `{ root: ElementRef, rootMargin: '100px', threshold: 0 }`
   *
   * {@link https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#creating_an_intersection_observer | IntersectionObserver API - MDN}
   */
  options?: IntersectionObserverInit
}

export function useIntersectionObserver<T>({
  callback = () => {},
  options
}: IntesectionObserverProps = {}) {
  const [alreadyViewed, setAlreadyViewed] = useState<boolean>(false)
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false)

  const observeElementRef = useRef<T | null>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !alreadyViewed) {
        setAlreadyViewed(true)
      }

      setIsIntersecting(entry.isIntersecting)

      callback(entry.isIntersecting)
    }, options)

    if (observeElementRef.current != null) {
      observer.observe(observeElementRef.current as T as Element)
    }

    return () => {
      observer.disconnect()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback, options])

  return { observeElementRef, isIntersecting, alreadyViewed }
}
